// La edición en el visor del TELÉFONO — ADR-2026-10-28-editar-en-el-visor-del-celular.
//
// Es la misma edición que la de escritorio (`BarraDelVisor` + `EdicionEnElVisor`), pensada
// para el pulgar como Fotos de iPhone, Google Photos o CapCut: abajo de la imagen, la caja
// del prompt y DEBAJO una fila horizontal de herramientas (ícono arriba, nombre abajo).
// Tocar una herramienta esconde la caja y la fila pasa a mostrar las opciones de esa
// herramienta, con un ‹ a la izquierda para volver; arriba de la fila, el nombre de la
// herramienta y su acción ("Guardar versión", "Expandir"…).
//
// La lógica no está acá: el envío del prompt es `usePromptDeEdicion` y lo que hace cada
// herramienta, `useHerramientasDeEdicion`, los mismos que usa escritorio. Separar en capas
// es una versión simple de la de escritorio: separa, y cada capa se muestra u oculta
// tocándola en la fila; no se arrastra ni se edita con IA
// (ADR-2026-10-28-capas-simples-en-el-celular).

import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, Download, EyeOff, Plus, RotateCcw, Search, Stamp } from 'lucide-react';
import { ASPECT_OPTIONS, ImageCropCanvas, useCropState } from '../ImageCropModal';
import { BotonConCreditos } from './BotonConCreditos';
import { useCalceSobre } from './piezasDelPanel';
import { LienzoDeCapas, TABLERO } from './ModoCapas';
import {
  MiniaturaDeEleccion,
  useProductosParaElegir,
  VistaPreviaDeExpansion,
  type ItemDeEleccion,
} from './EdicionEnElVisor';
import { useHerramientasDeEdicion, type HerramientaDeEdicion, type ImagenEnEdicion, type OpcionesDeHerramientas } from './useHerramientasDeEdicion';
import {
  AdjuntosDelPrompt,
  ajustarAlto,
  ALTO_MAXIMO_TEXTO,
  ContenidoDelAviso,
  CREDITOS_POR_EDICION,
  usePromptDeEdicion,
  type OpcionesDelPrompt,
} from './promptDeEdicion';
import { ModalDeFuentes } from '../../chat-atlas/ui/ModalDeFuentes';
import { CREDITOS_EXPANSION, ladoQueCrece, opcionesParaImagen, type RatioExpansion } from '../../lib/expansionMagica';
import { mayorEscalaQueEntra, upscaleEntraEnStorage, type Dimensiones } from '../../lib/upscaleLimits';
import { formatoDeMedidas } from '../../lib/versionesDelVisor';
import { CREDITOS_SEPARAR_EN_CAPAS, sinCambios, type CapaEnLienzo, type Separacion } from '../../lib/separarEnCapas';
import { usePalabraDeModelo } from '../../lib/nombreDeModelo';
import type { ModelV2 } from '../../pages/v2/modelsApi';
import { fotoDe, useModelos } from '../../pages/flujos/panels/catalogoPersonas';

/** 16 px: con menos, iOS hace zoom al enfocar el campo. La capa que resalta las `@menciones`
 *  usa las mismas clases (tienen que calcar glifo por glifo). */
const CLASES_DEL_TEXTO = 'py-2 text-[16px] leading-[22px]';
/** Aire alrededor de la imagen en el lienzo del recorte, en px (el mismo que escritorio). */
const MARGEN_RECORTE = 24;

/** El nombre de la herramienta para debajo del ícono: "Cambiar producto" → "Producto". */
const etiquetaCorta = (nombre: string) => {
  if (nombre === 'Separar en capas') return 'Capas';
  const corto = nombre.replace(/^Cambiar /, '');
  return corto.charAt(0).toUpperCase() + corto.slice(1);
};

/** Una opción de la fila (proporción, formato, escala): una pastilla de vidrio. */
const Opcion: React.FC<{
  activa: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}> = ({ activa, disabled, onClick, children, className = '' }) => (
  <button
    type="button"
    disabled={disabled}
    aria-pressed={activa}
    onClick={onClick}
    className={`flex h-11 shrink-0 flex-col items-center justify-center rounded-md px-4 transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.96] disabled:active:scale-100 ${
      disabled
        ? 'cursor-not-allowed bg-white/[0.03] text-white/25'
        : activa
          ? 'bg-white text-brand-black'
          : 'bg-white/[0.08] text-white/80'
    } ${className}`}
  >
    {children}
  </button>
);

/** La fila que scrollea de costado, sin barra. */
const Fila: React.FC<{ children: ReactNode; etiqueta: string }> = ({ children, etiqueta }) => (
  <div role="group" aria-label={etiqueta} data-sin-deslizar className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overscroll-x-contain scrollbar-hide" style={{ touchAction: 'pan-x' }}>
    {children}
  </div>
);

// ── Las opciones de cada herramienta ────────────────────────────────────────────
// Cada una devuelve las dos partes de la vista de una herramienta: `accion` (el botón de
// arriba a la derecha) y `opciones` (la fila). Los estados viven en el componente de la
// herramienta, así que se dibujan juntas.

interface VistaDeHerramienta {
  /** Una línea bajo el título: qué hace, qué cuesta o un aviso. */
  ayuda?: ReactNode;
  accion: ReactNode;
  opciones: ReactNode;
  /** Algo a lo ancho ARRIBA de la fila (el buscador de productos). */
  encima?: ReactNode;
  /** Lo que hace el ‹ en vez de volver a las herramientas (Capas pide confirmar antes de
   *  tirar lo separado). */
  alVolver?: () => void;
}

const Recortar: React.FC<{
  imagen: ImagenEnEdicion;
  imgRef: RefObject<HTMLImageElement>;
  /** El área de la imagen en el visor: el lienzo del recorte la ocupa entera. */
  areaRef: RefObject<HTMLElement>;
  ocupado: boolean;
  onGuardar: (area: { x: number; y: number; width: number; height: number }, formato?: string) => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ imagen, imgRef, areaRef, ocupado, onGuardar, children }) => {
  // El lienzo ocupa el ÁREA de la imagen (no la imagen más un margen, que en una foto alta
  // se metía debajo del ‹ y de la fila) y la imagen entra adentro con MARGEN_RECORTE de
  // aire para agarrar las esquinas. La imagen del visor se esconde: el lienzo dibuja la
  // suya, y con las dos se veían dos capas (ADR-2026-10-28-el-recorte-del-telefono-entra-en-su-area).
  const recorte = useCropState(imagen.srcOriginal, imagen.fileName, () => {}, { margen: MARGEN_RECORTE, sombraSoloEnLaImagen: true });
  const rect = useCalceSobre(areaRef, true);
  useEffect(() => {
    const img = imgRef.current;
    if (img) img.style.visibility = 'hidden';
    return () => {
      if (img) img.style.visibility = '';
    };
  }, [imgRef]);
  const area = recorte.areaNormalizada;
  const esEntera = !area || (area.x < 0.001 && area.y < 0.001 && area.width > 0.999 && area.height > 0.999);
  const opcion = ASPECT_OPTIONS[recorte.selectedAspect];
  const formato =
    opcion?.ratio && opcion.label.includes(':') ? opcion.label : formatoDeMedidas(recorte.realWidth, recorte.realHeight);
  return (
    <>
      {rect &&
        createPortal(
          // Por encima del visor (z 50) y por portal: dentro de la fila quedaría atado a ella.
          <div className="fixed z-[55] animate-fade-in" style={{ ...rect, touchAction: 'none' }}>
            <ImageCropCanvas cropState={recorte} fondoClassName="bg-transparent" />
          </div>,
          document.body,
        )}
      {children({
        ayuda: recorte.imageLoaded ? `${recorte.realWidth} × ${recorte.realHeight} px · gratis` : 'Arrastrá las esquinas sobre la imagen',
        accion: (
          <BotonConCreditos ocupado={ocupado} disabled={!recorte.imageLoaded || esEntera} onClick={() => area && onGuardar(area, formato)}>
            {ocupado ? 'Guardando…' : 'Guardar'}
          </BotonConCreditos>
        ),
        opciones: (
          <>
            {ASPECT_OPTIONS.map((o, i) => (
              <Opcion key={o.label} activa={recorte.selectedAspect === i} onClick={() => recorte.handleAspectChange(i)}>
                <span className="text-[14px] font-medium">{o.label}</span>
              </Opcion>
            ))}
            <Opcion activa={false} onClick={recorte.handleReset}>
              <span className="inline-flex items-center gap-1.5 text-[14px] font-medium">
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Restablecer
              </span>
            </Opcion>
            <Opcion activa={false} disabled={!recorte.imageLoaded || ocupado} onClick={recorte.handleCropAndDownload}>
              <span className="inline-flex items-center gap-1.5 text-[14px] font-medium">
                <Download className="h-4 w-4" aria-hidden="true" />
                Descargar
              </span>
            </Opcion>
          </>
        ),
      })}
    </>
  );
};

const Expandir: React.FC<{
  imgRef: RefObject<HTMLImageElement>;
  imagen: ImagenEnEdicion;
  medidas: Dimensiones | null;
  ocupado: boolean;
  areaLibre: () => { w: number; h: number };
  onExpandir: (r: RatioExpansion) => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ imgRef, imagen, medidas, ocupado, areaLibre, onExpandir, children }) => {
  const w = medidas?.width ?? 0;
  const h = medidas?.height ?? 0;
  const opciones = useMemo(() => opcionesParaImagen(w, h), [w, h]);
  const [ratio, setRatio] = useState<RatioExpansion | null>(null);
  useEffect(() => {
    if (ratio !== null) return;
    const primera = opciones.find((o) => o.disponible);
    if (primera) setRatio(primera.id);
  }, [opciones, ratio]);
  const crece = ratio ? ladoQueCrece(ratio, w, h) : null;
  return (
    <>
      {/* Por portal: la fila entra con una animación de `transform`, y adentro de ella un
          `fixed` se mediría desde la fila y no desde la pantalla. La capa va encima del
          visor (z 50) y no toma toques. */}
      {ratio &&
        createPortal(
          <div className="pointer-events-none fixed inset-0 z-[51]">
            <VistaPreviaDeExpansion imgRef={imgRef} src={imagen.srcVisible} ratio={ratio} ocupado={ocupado} areaLibre={areaLibre} />
          </div>,
          document.body,
        )}
      {children({
        ayuda: crece ? `Crece hacia ${crece === 'costados' ? 'los costados' : 'arriba y abajo'}: la IA completa lo nuevo` : 'Elegí el formato nuevo',
        accion: (
          <BotonConCreditos creditos={CREDITOS_EXPANSION} ocupado={ocupado} disabled={!ratio || !medidas} onClick={() => ratio && onExpandir(ratio)}>
            {ocupado ? 'Expandiendo…' : 'Expandir'}
          </BotonConCreditos>
        ),
        opciones: opciones.map((o) => (
          <Opcion key={o.id} activa={ratio === o.id} disabled={!o.disponible || ocupado} onClick={() => setRatio(o.id)} className="min-w-[72px]">
            <span className="text-[14px] font-semibold leading-none">{o.id}</span>
            <span className="mt-1 text-[11px] leading-none opacity-70">{o.etiqueta}</span>
          </Opcion>
        )),
      })}
    </>
  );
};

const Mejorar: React.FC<{
  imagen: ImagenEnEdicion;
  medidas: Dimensiones | null;
  ocupado: boolean;
  onMejorar: (escala: 2 | 4) => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ imagen, medidas, ocupado, onMejorar, children }) => {
  const [escala, setEscala] = useState<2 | 4>(2);
  const maxima = medidas ? mayorEscalaQueEntra(medidas) : 4;
  useEffect(() => {
    if (medidas && !upscaleEntraEnStorage(medidas, escala) && maxima) setEscala(maxima);
  }, [medidas, escala, maxima]);
  const costo = escala === 2 ? 1 : 2;
  const ayuda =
    medidas && maxima === null ? (
      <span className="text-amber-200/90">Esta imagen es demasiado grande para mejorarla más</span>
    ) : imagen.yaMejorada ? (
      <span className="text-amber-200/90">Ya tiene una versión mejorada: la reemplazás</span>
    ) : (
      `Sube ${escala} veces la resolución con IA`
    );
  return (
    <>
      {children({
        ayuda,
        accion: (
          <BotonConCreditos creditos={costo} ocupado={ocupado} disabled={!!medidas && maxima === null} onClick={() => onMejorar(escala)}>
            {ocupado ? 'Mejorando…' : 'Mejorar'}
          </BotonConCreditos>
        ),
        opciones: ([2, 4] as const).map((e) => {
          const noEntra = medidas ? !upscaleEntraEnStorage(medidas, e) : false;
          return (
            <Opcion key={e} activa={escala === e} disabled={noEntra || ocupado} onClick={() => setEscala(e)} className="min-w-[72px]">
              <span className="text-[15px] font-semibold">{e}x</span>
            </Opcion>
          );
        }),
      })}
    </>
  );
};

/** Cuántas miniaturas se dibujan por tanda en el carrusel. */
const TANDA_DEL_CARRUSEL = 10;
/** Alto de las miniaturas del carrusel: lo bastante para reconocer el producto o la persona. */
const ALTO_DEL_CARRUSEL = 88;

/**
 * El carrusel de productos o modelos: miniaturas en la fila, la elegida con anillo blanco.
 * Filtra por `busqueda`, que escribe el buscador de ARRIBA de la fila (`BuscadorDeLaFila`).
 * Dibuja de a tandas y suma otra cuando el final entra a la
 * vista, como la grilla de escritorio: las miniaturas que no se dibujan no se bajan.
 */
const Carrusel: React.FC<{
  items: ItemDeEleccion[] | null;
  elegido: string | null;
  onElegir: (id: string) => void;
  /** Ancho de la miniatura; el alto es `ALTO_DEL_CARRUSEL`. */
  ancho: number;
  vacio: string;
  disabled?: boolean;
  busqueda?: string;
}> = ({ items, elegido, onElegir, ancho, vacio, disabled, busqueda = '' }) => {
  const [dibujadas, setDibujadas] = useState(TANDA_DEL_CARRUSEL);
  useEffect(() => setDibujadas(TANDA_DEL_CARRUSEL), [busqueda]);
  const filtro = busqueda.trim().toLowerCase();
  const visibles = items ? (filtro ? items.filter((it) => it.nombre.toLowerCase().includes(filtro)) : items) : null;
  const quedan = !!visibles && dibujadas < visibles.length;

  const finRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const fin = finRef.current;
    if (!fin || !quedan) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) setDibujadas((n) => n + TANDA_DEL_CARRUSEL);
      },
      { rootMargin: '0px 200px 0px 0px' },
    );
    obs.observe(fin);
    return () => obs.disconnect();
  }, [quedan, dibujadas]);

  if (visibles === null)
    return (
      <>
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className="block shrink-0 animate-pulse rounded-sm bg-white/[0.08]" style={{ width: ancho, height: ALTO_DEL_CARRUSEL }} />
        ))}
      </>
    );
  return (
    <>
      {visibles.length === 0 ? (
        <p className="shrink-0 px-1 text-[13px] text-white/55">{filtro ? `Nada coincide con "${busqueda.trim()}".` : vacio}</p>
      ) : (
        <div role="listbox" className="flex shrink-0 items-center gap-2">
          {visibles.slice(0, dibujadas).map((it) => {
            const activo = it.id === elegido;
            return (
              <button
                key={it.id}
                type="button"
                role="option"
                aria-selected={activo}
                aria-label={it.nombre}
                title={it.nombre}
                disabled={disabled}
                onClick={() => onElegir(it.id)}
                className="version-entra relative block shrink-0 overflow-hidden rounded-md bg-white/[0.08] transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-50"
                style={{ width: ancho, height: ALTO_DEL_CARRUSEL }}
              >
                {/* Con una elegida, las demás se apagan: la elegida se lee de un vistazo. El
                    apagado va en una capa interna: `version-entra` (fill `both`) fija la
                    opacidad del botón y pisaría la clase. */}
                <span
                  className={`absolute inset-0 transition-opacity duration-150 ease-out ${elegido && !activo ? 'opacity-40' : 'opacity-100'}`}
                >
                  <MiniaturaDeEleccion item={it} />
                </span>
                <span
                  aria-hidden="true"
                  className={`absolute inset-0 rounded-md ring-inset transition-[box-shadow] duration-150 ${activo ? 'ring-[3px] ring-white' : 'ring-0 ring-transparent'}`}
                />
                {/* La marca de elegida: el tilde en la esquina, con el azul de la marca. */}
                {activo && (
                  <span
                    aria-hidden="true"
                    className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-brand-primary text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)] animate-fade-in"
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          {quedan && <span ref={finRef} aria-hidden="true" className="block h-px w-px shrink-0" />}
        </div>
      )}
    </>
  );
};

/** El buscador del carrusel, arriba de la fila y a lo ancho (con 16 px de letra: con menos,
 *  iOS hace zoom al enfocarlo). */
const BuscadorDeLaFila: React.FC<{ valor: string; onCambiar: (v: string) => void; etiqueta: string }> = ({ valor, onCambiar, etiqueta }) => (
  <label className="mx-4 flex h-11 items-center gap-2 rounded-md bg-white/[0.08] px-3 ring-white/25 focus-within:ring-1">
    <Search className="h-4 w-4 shrink-0 text-white/45" aria-hidden="true" />
    <input
      type="search"
      value={valor}
      onChange={(e) => onCambiar(e.target.value)}
      placeholder={etiqueta}
      aria-label={etiqueta}
      className="min-w-0 flex-1 bg-transparent text-[16px] text-white outline-none placeholder:text-white/40"
    />
  </label>
);

const CambiarProducto: React.FC<{
  companyId: string | null;
  ocupado: boolean;
  onCambiar: (varianteId: string) => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ companyId, ocupado, onCambiar, children }) => {
  const items = useProductosParaElegir(companyId);
  const [elegido, setElegido] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const nombre = items?.find((i) => i.id === elegido)?.nombre;
  return (
    <>
      {children({
        encima: items && items.length > 0 ? <BuscadorDeLaFila valor={busqueda} onCambiar={setBusqueda} etiqueta="Buscar productos" /> : undefined,
        ayuda: nombre ?? 'Elegí el producto que va en la foto',
        accion: (
          <BotonConCreditos creditos={1} ocupado={ocupado} disabled={!elegido} onClick={() => elegido && onCambiar(elegido)}>
            {ocupado ? 'Cambiando…' : 'Cambiar'}
          </BotonConCreditos>
        ),
        opciones: (
          <Carrusel
            items={items}
            elegido={elegido}
            onElegir={setElegido}
            ancho={76}
            vacio="Todavía no cargaste productos en esta marca."
            disabled={ocupado}
            busqueda={busqueda}
          />
        ),
      })}
    </>
  );
};

const CapasSimples: React.FC<{
  imgRef: RefObject<HTMLImageElement>;
  ocupado: boolean;
  onSeparar: (instruccion: string | undefined, alTerminar: (s: Separacion) => void) => void;
  onGuardar: (datos: { capas: CapaEnLienzo[]; ancho: number; alto: number; formato?: string }) => void;
  onVolver: () => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ imgRef, ocupado, onSeparar, onGuardar, onVolver, children }) => {
  const [separacion, setSeparacion] = useState<Separacion | null>(null);
  const [capas, setCapas] = useState<CapaEnLienzo[]>([]);
  /** El primer ‹ con capas separadas pide confirmar: el crédito ya se usó. */
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  const rect = useCalceSobre(imgRef, !!separacion);

  // Con capas, la imagen del visor se esconde: las capas (la base incluida) la reemplazan,
  // como en escritorio. `visibility` y no `opacity`: la opacidad la maneja el visor.
  useEffect(() => {
    if (!separacion) return;
    const img = imgRef.current;
    if (img) img.style.visibility = 'hidden';
    return () => {
      if (img) img.style.visibility = '';
    };
  }, [separacion, imgRef]);

  const alternar = (id: string) => {
    setConfirmarDescarte(false);
    setCapas((cs) => cs.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)));
  };

  if (!separacion)
    return (
      <>
        {children({
          ayuda: 'Cada elemento en su capa',
          accion: (
            <BotonConCreditos
              creditos={CREDITOS_SEPARAR_EN_CAPAS}
              ocupado={ocupado}
              onClick={() =>
                onSeparar(undefined, (s) => {
                  setSeparacion(s);
                  setCapas(s.capas);
                })
              }
            >
              {ocupado ? 'Separando…' : 'Separar'}
            </BotonConCreditos>
          ),
          opciones: (
            <p className="min-w-0 flex-1 pr-4 text-[13px] leading-snug text-white/55">
              Después tocás cada capa para mostrarla u ocultarla.
            </p>
          ),
        })}
      </>
    );

  const igual = sinCambios(capas, separacion.capas);
  const nadaVisible = capas.every((c) => !c.visible);
  // Adelante primero, como en la lista de escritorio; la base (el fondo) al final.
  const enFila = [...capas].reverse();
  return (
    <>
      {rect && (
        <LienzoDeCapas
          rect={rect}
          capas={capas}
          abiertas={false}
          en3D={false}
          interactivo={false}
          ocupado={ocupado}
          seleccion={null}
          resaltada={null}
          onSeleccionar={() => {}}
          onCambiar={() => {}}
          capaEnProceso={null}
        />
      )}
      {children({
        ayuda: confirmarDescarte ? (
          <span className="text-amber-200/90">Tocá ‹ otra vez para descartar las capas</span>
        ) : (
          `${capas.length - 1} ${capas.length - 1 === 1 ? 'capa' : 'capas'} y el fondo · tocá para ocultar`
        ),
        accion: (
          <BotonConCreditos
            ocupado={ocupado}
            disabled={igual || nadaVisible}
            onClick={() =>
              onGuardar({ capas, ancho: separacion.ancho, alto: separacion.alto, formato: formatoDeMedidas(separacion.ancho, separacion.alto) })
            }
          >
            {ocupado ? 'Guardando…' : 'Guardar'}
          </BotonConCreditos>
        ),
        alVolver: () => {
          if (!confirmarDescarte) return setConfirmarDescarte(true);
          onVolver();
        },
        opciones: enFila.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={c.visible}
            aria-label={`${c.esBase ? 'Fondo' : c.nombre}: ${c.visible ? 'ocultar' : 'mostrar'}`}
            disabled={ocupado}
            onClick={() => alternar(c.id)}
            className="version-entra flex w-14 shrink-0 flex-col items-center gap-1 transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-50"
          >
            <span className="relative block h-12 w-12 overflow-hidden rounded-sm" style={TABLERO}>
              <img
                src={c.src}
                alt=""
                className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-150 ${c.visible ? 'opacity-100' : 'opacity-25'}`}
              />
              {!c.visible && (
                <span className="absolute inset-0 grid place-items-center bg-black/40 animate-fade-in">
                  <EyeOff className="h-4 w-4 text-white" aria-hidden="true" />
                </span>
              )}
            </span>
            <span className={`w-full truncate text-center text-[10.5px] font-medium ${c.visible ? 'text-white/80' : 'text-white/40'}`}>
              {c.esBase ? 'Fondo' : c.nombre}
            </span>
          </button>
        )),
      })}
    </>
  );
};

const CambiarModelo: React.FC<{
  companyId: string | null;
  ocupado: boolean;
  onCambiar: (modelo: ModelV2) => void;
  children: (v: VistaDeHerramienta) => ReactNode;
}> = ({ companyId, ocupado, onCambiar, children }) => {
  const palabra = usePalabraDeModelo();
  // Refrescando: un modelo creado durante la sesión tiene que aparecer acá.
  const modelos = useModelos(companyId, { refrescar: true });
  const [elegido, setElegido] = useState<string | null>(null);
  const conFoto = modelos?.filter((m) => !!m.fotoFileName) ?? null;
  const modelo = conFoto?.find((m) => String(m.id) === elegido) ?? null;
  return (
    <>
      {children({
        ayuda: modelo?.nombre ?? 'Misma pose, misma luz, otra persona',
        accion: (
          <BotonConCreditos creditos={1} ocupado={ocupado} disabled={!modelo} onClick={() => modelo && onCambiar(modelo)}>
            {ocupado ? 'Cambiando…' : 'Cambiar'}
          </BotonConCreditos>
        ),
        opciones: (
          <Carrusel
            items={conFoto?.map((m) => ({ id: String(m.id), nombre: m.nombre, imagen: fotoDe(m) })) ?? null}
            elegido={elegido}
            onElegir={setElegido}
            ancho={66}
            vacio={`Todavía no tenés ${palabra.plural} guardados.`}
            disabled={ocupado}
          />
        ),
      })}
    </>
  );
};

// ── La barra ────────────────────────────────────────────────────────────────────

export function EdicionEnElVisorMovil({
  imgRef,
  areaRef,
  imagen,
  herramienta,
  onHerramienta,
  onDescargar,
  descargando = false,
  onMarcaDeAgua,
  volverRef,
  prompt: opcionesDelPrompt,
  ...opcionesDeHerramientas
}: {
  /** La `<img>` del visor del teléfono: el recorte y la vista previa de Expandir se calzan
   *  sobre ella. */
  imgRef: RefObject<HTMLImageElement>;
  /** El área donde vive la imagen: la expansión achica la imagen para que entre ahí. */
  areaRef: RefObject<HTMLElement>;
  herramienta: HerramientaDeEdicion | null;
  onHerramienta: (h: HerramientaDeEdicion | null) => void;
  onDescargar?: () => void;
  descargando?: boolean;
  /** Abre el editor de marca de agua (lo dibuja el visor, que tiene su estado). Sin él, no
   *  hay botón. */
  onMarcaDeAgua?: () => void;
  /** Con una herramienta abierta, el visor deja acá lo que hace su ‹: así el volver de
   *  arriba sale de la herramienta por el MISMO camino, con la confirmación de Capas
   *  incluida (ADR-2026-10-28-revision-216-editar-en-el-telefono). Sin herramienta, null. */
  volverRef?: MutableRefObject<(() => void) | null>;
  prompt: Omit<OpcionesDelPrompt, 'companyId'>;
} & Omit<OpcionesDeHerramientas, 'onTerminar'>) {
  const { companyId } = opcionesDeHerramientas;
  const prompt = usePromptDeEdicion({ ...opcionesDelPrompt, companyId });
  const { inputRef, instruccion, setInstruccion, enviando, listo, aviso, fuentes, setFuentes, menus, enviar } = prompt;

  // Hacia dónde entra la fila: de la derecha al abrir una herramienta, de la izquierda al volver.
  const [sentido, setSentido] = useState<'adelante' | 'atras'>('adelante');
  const abrir = (h: HerramientaDeEdicion | null) => {
    setSentido(h ? 'adelante' : 'atras');
    onHerramienta(h);
  };
  const h = useHerramientasDeEdicion({ ...opcionesDeHerramientas, imagen, onTerminar: () => abrir(null) });
  const herramientas = h.herramientasVisibles;
  const actual = herramienta ? herramientas.find((x) => x.id === herramienta) ?? null : null;
  const ocupado = h.ocupado;

  // La caja se desmonta con una herramienta abierta y vuelve con `rows={1}`: el alto se
  // ajusta también al volver, no sólo al tipear, o un borrador largo quedaba en un renglón.
  useEffect(() => {
    if (!actual && inputRef.current) ajustarAlto(inputRef.current);
  }, [actual, inputRef]);

  /** El lugar que tiene la imagen, para la vista previa de Expandir (menos el aire del área). */
  const areaLibre = () => {
    const r = areaRef.current?.getBoundingClientRect();
    return r ? { w: Math.max(120, r.width - 24), h: Math.max(120, r.height - 24) } : { w: window.innerWidth - 24, h: window.innerHeight * 0.5 };
  };

  /** La vista de una herramienta: título y ayuda arriba a la izquierda, la acción a la
   *  derecha, y debajo ‹ + la fila de opciones. */
  const volverDeLaVista = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!volverRef) return;
    volverRef.current = actual ? () => volverDeLaVista.current?.() : null;
    return () => {
      volverRef.current = null;
    };
  });
  const vista = ({ ayuda, accion, opciones, alVolver, encima }: VistaDeHerramienta) => {
    volverDeLaVista.current = alVolver ?? (() => abrir(null));
    return (
    // Las opciones arriba (cerca de la imagen, que es donde se ve el efecto) y abajo, al
    // alcance del pulgar, el título, la ayuda y la acción.
    <div className="flex flex-col gap-3">
      {encima}
      <div className="flex items-center gap-2 pl-4">
        <button
          type="button"
          onClick={() => (alVolver ? alVolver() : abrir(null))}
          disabled={ocupado}
          aria-label="Volver a las herramientas"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/[0.08] text-white/85 transition-[background-color,transform] duration-150 ease-out active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <span aria-hidden="true" className="h-7 w-px shrink-0 bg-white/15" />
        <Fila etiqueta={`Opciones de ${actual?.nombre ?? 'la herramienta'}`}>
          {opciones}
          {/* Aire al final: el último no queda pegado al borde de la pantalla. */}
          <span aria-hidden="true" className="block w-2 shrink-0" />
        </Fila>
      </div>
      <div className="flex min-h-10 items-center gap-3 px-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-white">{actual?.nombre}</p>
          {ayuda && <p className="mt-0.5 truncate text-[12.5px] text-white/55">{ayuda}</p>}
        </div>
        {accion}
      </div>
    </div>
    );
  };

  return (
    <div className="relative flex flex-col gap-3 text-white">
      {/* La caja del prompt: se va con una herramienta abierta (no sirve para recortar ni
          expandir) y vuelve al cerrar. */}
      {!actual && (
        <div className="animate-fade-in px-4">
          {aviso && (
            <div className="mb-2 animate-fade-in rounded-lg border border-white/[0.08] bg-black/60 px-4 py-3 backdrop-blur-xl">
              <ContenidoDelAviso
                prompt={prompt}
                claseBoton="inline-flex shrink-0 items-center rounded-md bg-white/10 px-4 py-2 text-[13.5px] font-medium text-white transition-transform duration-150 ease-out active:scale-[0.96]"
              />
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void enviar();
            }}
            className="flex flex-col gap-2 rounded-[28px] border border-white/[0.08] bg-black/20 p-3 pt-2 backdrop-blur-xl"
          >
            {/* La capa espejo (`menus.resaltado`) es `absolute inset-0`: lleva el MISMO `px`
                que este envoltorio (ver la nota de `BarraDelVisor`). */}
            <div className="relative min-w-0 px-2">
              {menus.resaltado(`${CLASES_DEL_TEXTO} px-2 text-white`)}
              <textarea
                ref={inputRef}
                rows={1}
                value={instruccion}
                onScroll={menus.alScrollear}
                onChange={(e) => {
                  setInstruccion(e.target.value);
                  ajustarAlto(e.currentTarget);
                  menus.releerToken(e.target);
                }}
                onSelect={(e) => menus.releerToken(e.currentTarget)}
                onBlur={(e) => menus.alDesenfocar(e)}
                onKeyDown={(e) => {
                  if (menus.alTeclear(e)) return;
                  // "Enviar" del teclado del teléfono (o Enter) edita; Shift+Enter baja de renglón.
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void enviar();
                  }
                }}
                disabled={enviando}
                enterKeyHint="send"
                placeholder="¿Qué querés cambiar?"
                aria-label="Qué querés cambiar de la imagen"
                className={`relative w-full resize-none bg-transparent ${CLASES_DEL_TEXTO} text-transparent caret-white outline-none placeholder:text-white/40 disabled:opacity-60`}
                style={{ maxHeight: ALTO_MAXIMO_TEXTO }}
              />
              {menus.menus}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFuentes('productos')}
                disabled={enviando}
                aria-label="Añadir una referencia"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/[0.08] text-white transition-[background-color,transform] duration-150 ease-out active:scale-[0.96] disabled:opacity-60"
              >
                <Plus className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
              </button>
              <AdjuntosDelPrompt prompt={prompt} />
              <span className="flex-1" />
              <BotonConCreditos type="submit" creditos={CREDITOS_POR_EDICION} ocupado={enviando} listo={listo} disabled={!instruccion.trim()}>
                {enviando ? 'Editando…' : 'Editar'}
              </BotonConCreditos>
            </div>
          </form>
        </div>
      )}

      {/* La fila: las herramientas, o las opciones de la elegida. Entra deslizándose según
          el sentido (`key`). */}
      <div
        key={`${herramienta ?? 'lista'}:${imagen.id}`}
        className={sentido === 'adelante' ? 'panel-de-edicion-adelante' : 'panel-de-edicion-atras'}
      >
        {!actual ? (
          <Fila etiqueta="Herramientas de edición">
            <span aria-hidden="true" className="block w-2 shrink-0" />
            {herramientas.map(({ id, nombre, Icono }) => (
              <button
                key={id}
                type="button"
                disabled={ocupado || enviando}
                onClick={() => abrir(id)}
                className="flex w-[84px] shrink-0 flex-col items-center gap-1.5 transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-white/[0.08] text-white/90">
                  <Icono className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="w-full truncate text-center text-[12px] font-medium text-white/75">{etiquetaCorta(nombre)}</span>
              </button>
            ))}
            {onMarcaDeAgua && (
              <button
                type="button"
                disabled={ocupado || enviando}
                onClick={onMarcaDeAgua}
                className="flex w-[84px] shrink-0 flex-col items-center gap-1.5 transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-white/[0.08] text-white/90">
                  <Stamp className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="w-full truncate text-center text-[12px] font-medium text-white/75">Marca de agua</span>
              </button>
            )}
            {onDescargar && (
              <button
                type="button"
                disabled={ocupado || enviando || descargando}
                onClick={onDescargar}
                className="flex w-[84px] shrink-0 flex-col items-center gap-1.5 transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-40 disabled:active:scale-100"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-white/[0.08] text-white/90">
                  <Download className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="w-full truncate text-center text-[12px] font-medium text-white/75">{descargando ? 'Bajando…' : 'Descargar'}</span>
              </button>
            )}
            <span aria-hidden="true" className="block w-2 shrink-0" />
          </Fila>
        ) : herramienta === 'recortar' ? (
          <Recortar imagen={imagen} imgRef={imgRef} areaRef={areaRef} ocupado={ocupado} onGuardar={h.recortar}>
            {vista}
          </Recortar>
        ) : herramienta === 'expandir' ? (
          <Expandir imgRef={imgRef} imagen={imagen} medidas={h.medidas} ocupado={ocupado} areaLibre={areaLibre} onExpandir={h.expandir}>
            {vista}
          </Expandir>
        ) : herramienta === 'mejorar' ? (
          <Mejorar imagen={imagen} medidas={h.medidas} ocupado={ocupado} onMejorar={h.mejorar}>
            {vista}
          </Mejorar>
        ) : herramienta === 'producto' ? (
          <CambiarProducto companyId={companyId} ocupado={ocupado} onCambiar={h.cambiarProducto}>
            {vista}
          </CambiarProducto>
        ) : herramienta === 'modelo' ? (
          <CambiarModelo companyId={companyId} ocupado={ocupado} onCambiar={h.cambiarModelo}>
            {vista}
          </CambiarModelo>
        ) : herramienta === 'capas' ? (
          <CapasSimples imgRef={imgRef} ocupado={ocupado} onSeparar={h.separar} onGuardar={h.guardarLasCapas} onVolver={() => abrir(null)}>
            {vista}
          </CapasSimples>
        ) : null}
      </div>

      {/* El selector de fuentes del Agente (productos, modelos, subidas), portaleado al body. */}
      <ModalDeFuentes
        abierto={fuentes !== null}
        onCerrar={() => setFuentes(null)}
        onAdjuntar={prompt.agregarAdjunto}
        multiple
        adjuntosActuales={prompt.adjuntos}
        onQuitar={prompt.quitarAdjunto}
        pestanaInicial={fuentes ?? 'productos'}
        conPinterest
      />
    </div>
  );
}

export default EdicionEnElVisorMovil;
